import type { EmitFn } from 'vue';
import type { DcbVouchers } from '~/lib/shop-api';
import { tealiumLink, tealiumView } from '~/lib/tealium';
import { trackFBEvent } from '~/lib/tracking-fb';
import type { FetchError } from 'ofetch';
import { useRoute } from '#imports';
import { openModal } from '~/composables/modal';
import { sendBeacon } from '~/lib/beacon';

const DCB_MAILFORM_REGISTER_SUCCESS_ID = 'content.button.dcb.mailform.register.success';

type DcbSpecialField = {
  required: boolean;
  error?: string;
};

export type MailFormOptions = {
  emits: EmitFn<'mailSent'[]>;
  specialFields?: {
    code?: DcbSpecialField;
    reminder?: DcbSpecialField;
  };
  dcbOptions: Pick<DcbVouchers, 'identifier' | 'path' | 'partner'>;
  hideFormCallback: () => void;
};

export const useMailForm = ({
  emits,
  specialFields,
  dcbOptions,
  hideFormCallback,
}: MailFormOptions) => {
  const route = useRoute();
  const { dcbVouchers } = useShopApi();
  const mail = ref<string>('');
  const mail2 = ref<string>('');
  const phoneArea = ref<string>('');
  const phoneNumber = ref<string>('');
  const agb = ref<boolean>(false);
  const code = ref<string | undefined>(undefined);
  const reminder = ref(false);

  const showErrors = ref<boolean>(false);

  const responseErrors = ref<string[]>([]);

  const isValidMail = computed<boolean>(() => /(.*)@(.*)\.(.*)/.test(mail.value));
  const isValidMail2 = computed<boolean>(
    () => /(.*)@(.*)\.(.*)/.test(mail2.value) && mail.value === mail2.value
  );
  const isValidPhoneArea = computed<boolean>(() => /^01\d{2,4}$/.test(phoneArea.value));
  const isValidPhoneNumber = computed<boolean>(() => /^\d{5,}/.test(phoneNumber.value));

  const isValidCode = computed<boolean>(() => /\w{6,}/.test(code.value ?? ''));

  const errors = computed<Record<string, string | undefined>>(() => {
    const errorFields: Record<string, string> = {};

    if (!isValidMail.value) {
      errorFields.mail = 'Bitte eine gültige E-Mail-Adresse angeben.';
    }

    if (!isValidMail2.value) {
      errorFields.mail2 = 'Die E-Mail-Adressen müssen identisch sein.';
    }

    if (!isValidPhoneArea.value) {
      errorFields.phoneArea = 'Bitte eine gültige Vorwahl angeben.';
    }

    if (!isValidPhoneNumber.value) {
      errorFields.phoneNumber = 'Bitte eine gültige Rufnummer angeben.';
    }

    if (!agb.value) {
      errorFields.agb = 'Bitte den Teilnahmebedingungen und Datenschutzhinweisen zustimmen.';
    }

    const codeField = specialFields?.code;
    if (codeField && codeField.required && !isValidCode.value) {
      errorFields.code = codeField.error || 'Bitte das Feld ausfüllen';
    }

    return errorFields;
  });

  const visibleErrors = computed(() => (showErrors.value ? errors.value : {}));

  const mailformTrackingScope = () => dcbOptions.partner ?? dcbOptions.identifier;

  const sendVouchers = (extraParams: Partial<DcbVouchers> = {}) => {
    responseErrors.value = [];

    const params: DcbVouchers = {
      ...dcbOptions,
      email: mail.value,
      phoneArea: phoneArea.value,
      phoneNumber: phoneNumber.value,
      reminder: reminder.value,
      wtmc: route?.query?.wt_mc ? (route?.query?.wt_mc as string) : undefined,
      ...extraParams,
    };

    if (specialFields?.code) {
      params.couponCode = code.value;
    }
    if (specialFields?.reminder) {
      params.reminder = reminder.value;
    }
    return dcbVouchers(params)
      .then((response) => {
        sendBeacon('dcb-mailform-success', mailformTrackingScope());
        mail.value = '';
        emits('mailSent');

        tealiumLink(`${DCB_MAILFORM_REGISTER_SUCCESS_ID}.${mailformTrackingScope()}`);

        // send Tracking Pixel
        tealiumView({
          page_content_id: `dcb-registration.${mailformTrackingScope()}`,
        });

        trackFBEvent('CompleteRegistration', {
          content_name: `dcb-registration.${mailformTrackingScope()}`,
          status: 'success',
        } as any);

        setTimeout(() => {
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          });
        }, 1);
      })
      .catch((error: FetchError) => {
        const definedError = error?.data?.errors;

        sendBeacon(
          'dcb-mailform-error',
          mailformTrackingScope(),
          String(definedError ?? 'unknown')
        );

        tealiumLink({
          wt_link_id: `content.button.dcb.mailform.register.error.${mailformTrackingScope()}`,
          event_errorcause: definedError || error?.data?.error || 'unbekannt',
        });

        if (
          ['coupon_unknown', 'coupon_already_redeemed', 'coupon_code_invalid'].includes(
            definedError
          )
        ) {
          responseErrors.value = ['Der Code ist bereits eingelöst oder ist ungültig.'];
          return;
        }
        if (definedError === 'No valid Campaign found.') {
          responseErrors.value = ['Die Kampagne ist leider abgelaufen.'];
          return;
        }
        // DCB Error
        if (['999'].includes(definedError)) {
          responseErrors.value = [
            'Deine Rufnummer konnte nicht überprüft werden, bitte versuche es später erneut.<br /><br /><strong>Hinweis:</strong> An der Aktion können nur Telekom Kunden mit Laufzeitvertrag teilnehmen.',
          ];
          return;
        }
        // 125 error_request_access_denied
        // 531 error_psp_prepaid_not_allowed
        if (['125', '531'].includes(definedError)) {
          responseErrors.value = [
            'Bitte überprüfe die eingegebene Rufnummer.<br />An der Aktion können nur Telekom Kunden mit Laufzeitvertrag teilnehmen.',
          ];
          return;
        }
        // 512: error_psp_goods_not_allowed
        // 520: error_psp_customer_pay_activation
        if (['512', '520'].includes(definedError)) {
          openModal({
            heading: 'Nach der Registrierung nicht vergessen!',
            content: `<div class="unstyled-html">
Für deine Rufnummer liegt eine Drittanbietersperre für einen oder mehrere Anbieter vor. Beachte daher:
<ul>
  <li style="line-height: normal;">Um per Handyrechnung zu bezahlen, muss der jeweilige Anbieter unter <a href="https://www.drittanbieterinfo.de" target="_blank" rel="noopener">www.drittanbieterinfo.de</a> freigegeben sein. </li>
  <li style="line-height: normal;">Anschließend kannst du Handyrechnung in den Zahlungseinstellungen deines Smartphones hinterlegen bzw. beim Anbieter verwenden.</li>
</ul>
</div>`,
            buttons: [
              {
                label: 'Verstanden und registrieren',
                variant: 'primary',
                tag: 'button',
                type: 'button',
                click: (closeModal) => {
                  closeModal();
                  sendVouchers({ customerOverride: true });
                },
              },
            ],
          });
          return;
        }
        if (['phone_number_taken'].includes(definedError)) {
          hideFormCallback();
          return;
        }
        let msg =
          error?.data?.error ||
          'Beim Senden der Daten ist ein Fehler aufgetreten. Bitte versuche es später erneut.';
        if (msg === 'Invalid E-Mail.') {
          msg =
            'Beim Senden der Daten ist ein Fehler aufgetreten. Bitte versuche es später erneut.';
        }
        const statusCode = error.statusCode ?? 0;
        if (statusCode > 400 && statusCode < 500) {
          captureMessage(`DCB-Error: ${definedError}`, 'debug', {
            captureContext: {
              contexts: {
                error: (error as any) ?? undefined,
                data: error?.data,
                params: {
                  ...params,
                  email: undefined,
                  phoneArea: undefined,
                  phoneNumber: undefined,
                },
              },
              tags: {
                identifier: params.identifier,
                partner: params.partner,
              },
            },
          });
        }
        responseErrors.value = [msg];
      });
  };

  const submit = () => {
    showErrors.value = true;

    const hasError = !!Object.values(errors.value).find((value) => !!value);
    if (hasError) {
      sendBeacon('dcb-mailform-invalid', mailformTrackingScope(), ...Object.keys(errors.value));
      tealiumLink(`dcb.mailform.invalid-fields.${Object.keys(errors.value).join('.')}`);
      return;
    }
    sendBeacon('dcb-mailform-valid', mailformTrackingScope());
    tealiumLink(`dcb.mailform.valid`);

    sendVouchers();
  };

  onMounted(() => {
    sendBeacon('dcb-mailform-load', mailformTrackingScope());
    if (route.query?.code) {
      code.value = route.query.code as string;
    }
  });

  return {
    submit,
    mail,
    mail2,
    phoneArea,
    phoneNumber,
    agb,
    code,
    reminder,
    errors,
    visibleErrors,
    showErrors,
    responseErrors,
  };
};
